/**
 * Helpers for preserving and resolving post-login redirect intent (path + query).
 *
 * When an unauthenticated user hits a protected route (e.g. deep link to
 * /properties/123/lease-audits?tab=open), we redirect to login and store the
 * intended destination so we can send them back after login. Redirect intent is
 * preserved in two ways:
 *
 * 1. **Location state** – Used for in-app navigations within the login flow.
 *    State is lost on full page loads (magic link click in email, OAuth callback).
 *
 * 2. **Session storage** – Persists across full page loads so redirect intent
 *    survives the round-trip through email or an external OAuth provider.
 *
 * 3. **URL `next` param (magic link)** – When the user opens the magic link in a
 *    new tab, sessionStorage is empty there. The frontend passes the intended
 *    destination as a `next` query param when requesting the magic link; the
 *    backend includes it in the email link and redirects to `next` after token
 *    validation so the new tab lands on the right place.
 *
 * We support two shapes for state.from (legacy and current with pathname + search).
 * All redirect targets are validated so we never redirect to external or malformed URLs.
 */

import {
  buildTenantRootPath,
  isGlobalAuthenticatedPath,
  LOGIN,
  readTenantSlugFromPath,
} from './paths';

const LOGIN_REDIRECT_KEY = 'surface_login_redirect';

/**
 * Ensures the given internal path begins with `/~/:tenantSlug/`. If the path is
 * already tenant-scoped, it is returned untouched. If a tenant slug is unavailable,
 * the path is returned as-is and `LegacyTenantRedirect` will rewrite it once
 * the user is authenticated.
 */
export function withTenantPrefix(
  pathAndSearch: string,
  tenantSlug: string | null | undefined
): string {
  if (!pathAndSearch || !pathAndSearch.startsWith('/')) return pathAndSearch;
  if (!tenantSlug) return pathAndSearch;
  const [pathname, ...rest] = pathAndSearch.split(/(\?|#)/);
  if (readTenantSlugFromPath(pathname)) return pathAndSearch;
  if (isGlobalAuthenticatedPath(pathname)) return pathAndSearch;
  const remainder = rest.join('');
  const tenantRoot = buildTenantRootPath(tenantSlug);
  if (pathname === '/') {
    return `${tenantRoot}${remainder}`;
  }
  return `${tenantRoot}${pathname}${remainder}`;
}

/**
 * Query param keys allowed in the login URL (partner branding, context, invalid link).
 * Other params (e.g. tab) are destination-specific and only live in stored redirect / next.
 */
export const LOGIN_URL_PARAM_KEYS = [
  'partner_id',
  'partner_property_id',
  'context',
  'invalid',
] as const;

/**
 * Returns the query string for login URLs (no leading ?), keeping only keys in LOGIN_URL_PARAM_KEYS.
 * Use when building /login URLs so destination params (e.g. tab) are hidden.
 */
export function getLoginOnlySearch(search: string): string {
  const raw = search?.startsWith('?') ? search.slice(1) : (search ?? '');
  if (!raw.trim()) return '';
  const params = new URLSearchParams(raw);
  const allowed = new URLSearchParams();
  for (const key of LOGIN_URL_PARAM_KEYS) {
    const value = params.get(key);
    if (value !== null) allowed.set(key, value);
  }
  const out = allowed.toString();
  return out;
}

/**
 * True if pathname is part of the login flow. Such paths must never be used as
 * post-login redirect or as magic-link `next` (avoids redirecting back to login).
 * Includes /login, /login/..., and any path ending with /magic-link (e.g. base-path /lease-audits/magic-link).
 */
export function isLoginPath(pathname: string): boolean {
  if (!pathname || typeof pathname !== 'string') return false;
  const p = pathname.trim();
  return p === LOGIN || p.startsWith(`${LOGIN}/`) || p.endsWith('/magic-link');
}

/**
 * Type guard: true if `from` is a safe internal location object (pathname + optional search).
 * Rejects missing/invalid values, relative paths, and protocol-relative or absolute URLs.
 */
export function isSafeInternalPath(
  from: unknown
): from is { pathname: string; search?: string } {
  if (!from || typeof from !== 'object') return false;
  const obj = from as { pathname?: string; search?: string };
  const pathname = obj.pathname;
  return (
    typeof pathname === 'string' &&
    pathname.startsWith('/') &&
    !pathname.startsWith('//') &&
    !pathname.toLowerCase().startsWith('http')
  );
}

/**
 * Returns the redirect URL from location.state.from, or null if missing/invalid.
 * Handles both string pathname and { pathname, search } shapes.
 * Never returns a login-flow path (use fallback e.g. /?query instead).
 */
export function getRedirectToFromState(location: {
  state?: { from?: unknown };
}): string | null {
  const from = location.state?.from;
  let pathAndSearch: string | null = null;
  if (typeof from === 'string') {
    pathAndSearch =
      from.startsWith('/') && !from.startsWith('//') ? from : null;
  } else if (isSafeInternalPath(from)) {
    pathAndSearch = `${from.pathname}${from.search ?? ''}`;
  }
  if (pathAndSearch === null) return null;
  const pathname = pathAndSearch.split('?')[0];
  if (isLoginPath(pathname)) return null;
  return pathAndSearch;
}

/**
 * Returns the stored redirect path+search from sessionStorage without consuming it.
 * Use when building the magic link request so the link can include the destination
 * in the URL (works when the link is opened in a new tab, where sessionStorage is empty).
 * Never returns a login-flow path.
 */
export function getStoredLoginRedirect(): string | null {
  try {
    const stored = sessionStorage.getItem(LOGIN_REDIRECT_KEY);
    if (!stored) return null;
    const parsed = JSON.parse(stored) as unknown;
    if (!isSafeInternalPath(parsed)) return null;
    if (isLoginPath(parsed.pathname)) return null;
    return `${parsed.pathname}${parsed.search ?? ''}`;
  } catch {
    return null;
  }
}

/**
 * Saves the intended post-login destination (pathname + search) to sessionStorage.
 * Call this when redirecting an unauthenticated user to login (e.g. in AuthRequired)
 * so redirect intent survives full page loads (magic link, OAuth callback). Only
 * saves if the location is a safe internal path.
 */
export function saveLoginRedirect(pathname: string, search: string): void {
  const searchNorm = search ?? '';
  if (!isSafeInternalPath({ pathname, search: searchNorm })) return;
  try {
    sessionStorage.setItem(
      LOGIN_REDIRECT_KEY,
      JSON.stringify({ pathname, search: searchNorm })
    );
  } catch {
    // sessionStorage can throw in private mode or when full
  }
}

/**
 * Returns the URL to redirect to after login, or null. Checks location.state first
 * (for in-app navigations), then sessionStorage (for post–full-page-load). If
 * sessionStorage was used, the stored value is cleared after a valid read so we
 * don't redirect again on a later visit. Used in: (1) UnauthRequired when an
 * authenticated user hits /login; (2) AuthRequired when an
 * authenticated user lands on / after magic link or SSO, so we send them to the
 * intended deep link. Fall back to a default (e.g. paths.ASK) when null.
 */
export function getRedirectAfterLogin(location: {
  state?: { from?: unknown };
}): string | null {
  const fromState = getRedirectToFromState(location);
  if (fromState !== null) return fromState;

  try {
    const stored = sessionStorage.getItem(LOGIN_REDIRECT_KEY);
    if (!stored) return null;
    const parsed = JSON.parse(stored) as unknown;
    if (!isSafeInternalPath(parsed)) return null;
    if (isLoginPath(parsed.pathname)) {
      sessionStorage.removeItem(LOGIN_REDIRECT_KEY);
      return null;
    }
    sessionStorage.removeItem(LOGIN_REDIRECT_KEY);
    return `${parsed.pathname}${parsed.search ?? ''}`;
  } catch {
    return null;
  }
}
